import React, { useState, useEffect, useCallback } from 'react'
import moment from 'moment'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { doGetOrderHistory } from '@state/reducers/orderReducer'
import { FindOrdersQuery, OrderDto } from '@services/dtos'
import {
  Divider,
  Popover,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  IconButton,
  Box,
  Stack,
  capitalize,
  CircularProgress,
  Button,
} from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import DateFilterComponent, { DateRange } from './DateFilterComponent'
import { useLocation, useSearchParams } from 'react-router-dom'

type Props = {
  order: OrderDto
}

const RenderOrderDetails: React.FC<{
  order: OrderDto
  onDetailsClick: () => void
}> = ({ order, onDetailsClick }) => {
  return (
    <Paper sx={{ width: 500, p: 2 }}>
      <Box
        sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'end' }}
      >
        <Button
          size="small"
          sx={{ mb: 4 }}
          variant="outlined"
          onClick={onDetailsClick}
        >
          Voir Détails
        </Button>
      </Box>
      <Stack spacing={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Date de l&apos;examen</Typography>
          <Typography variant="body2" color="text.secondary">
            {moment(order.plannedDate).format('DD-MM-YYYY HH:mm')}
          </Typography>
        </Box>
        <Divider />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Acceuilli le</Typography>
          <Typography variant="body2" color="text.secondary">
            {moment(order.registeredAt).format('DD-MM-YYYY HH:mm')}
          </Typography>
        </Box>
        <Divider />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Prescripteur</Typography>
          <Typography variant="body2" color="text.secondary">
            {order.referringDoctor?.firstName} {order.referringDoctor?.lastName}
          </Typography>
        </Box>
        <Divider />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Code</Typography>
          <Typography variant="body2" color="text.secondary">
            {order.exam?.code}
          </Typography>
        </Box>
        <Divider />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Libellé</Typography>
          <Typography variant="body2" color="text.secondary">
            {order.exam?.label}
          </Typography>
        </Box>
        <Divider />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Site</Typography>
          <Typography variant="body2" color="text.secondary">
            {order.site?.label}
          </Typography>
        </Box>
        <Divider />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Médecin consultant</Typography>
          <Typography variant="body2" color="text.secondary">
            {order.consultingDoctor?.firstName}{' '}
            {order.consultingDoctor?.lastName}
          </Typography>
        </Box>
        <Divider />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Praticien</Typography>
          <Typography variant="body2" color="text.secondary">
            {order.attendingDoctor?.firstName} {order.attendingDoctor?.lastName}
          </Typography>
        </Box>
        <Divider />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Médecin traitant</Typography>
          <Typography variant="body2" color="text.secondary">
            {order.patient?.familyDoctor.firstName}{' '}
            {order.patient?.familyDoctor.lastName}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}

export const OrderHistoryComponent: React.FC<Props> = ({ order }) => {
  const dispatch = useAppDispatch()
  const orders = useAppSelector(({ order }) => order.orderHistory)
  const [isFetching, setIsFetching] = useState(false)
  const [dateRange, setDateRange] = useState<DateRange | null>(null)

  const [searchParams] = useSearchParams()
  const location = useLocation()

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedOrder, setSelectedOrder] = useState<OrderDto | null>(null)
  const [page, setPage] = useState(0)
  const itemsPerPage = 5

  const [query, setQuery] = useState<FindOrdersQuery>({
    patientId: order.patient.id,
    page,
    limit: itemsPerPage,
  })

  const handleMouseEnter = (
    order: OrderDto,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setSelectedOrder(order)
    setAnchorEl(event.currentTarget)
  }
  const handleMouseLeave = () => {
    setAnchorEl(null)
  }

  const handleDateRangeChange = (newDateRange: DateRange) => {
    setDateRange(newDateRange)
    setQuery((prev) => ({
      ...prev,
      startDate: newDateRange.startDate.toISOString(),
      endDate: newDateRange.endDate.toISOString(),
      page: 0,
    }))
    setPage(0)
  }

  const buildOrderUrl = useCallback(
    (orderId: number) => {
      const params = new URLSearchParams()
      params.set('orderId', String(orderId))
      return `${location.pathname}?${params.toString()}`
    },
    [searchParams, location.pathname],
  )

  const handleOpenInNewTab = (orderId: number) => {
    const url = buildOrderUrl(orderId)
    window.open(url, '_blank')
  }

  useEffect(() => {
    setIsFetching(true)
    dispatch(doGetOrderHistory(query)).finally(() => setIsFetching(false))
  }, [query, dispatch])

  useEffect(() => {
    setQuery((prev) => ({
      ...prev,
      page,
    }))
  }, [page])

  const open = Boolean(anchorEl)
  const pageCount = Math.floor(orders.totalCount / itemsPerPage)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: '100%',
        bgcolor: 'background.default',
        position: 'relative',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" fontWeight="bold">
          Historique
        </Typography>
        <DateFilterComponent
          dateRange={null}
          onDateRangeChange={handleDateRangeChange}
          disabled={isFetching}
        />
      </Stack>
      {isFetching && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <Box
        sx={{
          filter: isFetching ? 'blur(2px)' : 'none',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <List
          sx={{
            overflow: 'auto',
            py: 0,
            minHeight: 340,
          }}
        >
          {orders.datas.map((orderItem) => (
            <ListItem
              key={orderItem.id}
              onClick={(e) => handleMouseEnter(orderItem, e)}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <ListItemText
                primary={moment(orderItem.plannedDate).format('DD-MM-YYYY')}
                secondary={orderItem.medicalOrders
                  .map((mo) => mo.exam?.code)
                  .join(', ')}
                primaryTypographyProps={{
                  variant: 'body2',
                  color: 'text.primary',
                }}
                secondaryTypographyProps={{
                  variant: 'body2',
                }}
              />
              <Stack alignItems="flex-end">
                <Typography variant="body2" color="text.secondary">
                  {orderItem.site?.label}
                </Typography>
                {orderItem.referringDoctor && (
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color="text.secondary"
                  >
                    Dr{' '}
                    {capitalize(
                      orderItem.referringDoctor?.firstName?.[0] || '',
                    )}
                    .{capitalize(orderItem.referringDoctor?.lastName || '')}
                  </Typography>
                )}
              </Stack>
            </ListItem>
          ))}
        </List>
      </Box>
      {pageCount > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: 1,
            borderColor: 'divider',
            p: 1,
          }}
        >
          <IconButton
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="body1" color="text.secondary">
            {page} / {pageCount}
          </Typography>
          <IconButton
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            disabled={page === pageCount}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleMouseLeave}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
      >
        {selectedOrder && (
          <RenderOrderDetails
            order={selectedOrder}
            onDetailsClick={() => handleOpenInNewTab(selectedOrder.id)}
          />
        )}
      </Popover>
    </Box>
  )
}

export default OrderHistoryComponent
